import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import type { FieldNamesMarkedBoolean } from 'react-hook-form';
import { type ManEulaStatus, checkIsLatamSpecificData } from '../../../../services/schemas/accountsBackendCodec';
import { AccountStatus } from '../AccountStatus';
import { RevokeEulaDialog } from '../dialogs/RevokeEulaDialog';
import type { FormAccount } from '../formAccount.ts';
import { languageOptions } from '../LanguageSelect';

interface ModifiedAccountDataProps {
    formAccount: FormAccount;
    eulaStatus: ManEulaStatus | undefined;
    dirtyFields?: Partial<Readonly<FieldNamesMarkedBoolean<FormAccount>>>;
    oldFormAccount?: FormAccount;
}

const getNativeLanguageLabel = (languageCode: string | undefined): string => {
    if (!languageCode) {
        return '-';
    }
    const option = languageOptions.find(opt => opt.id === languageCode);
    return option?.nativeLabel || languageCode;
};

export const ModifiedAccountData = (props: ModifiedAccountDataProps) => {
    const { formAccount, dirtyFields, oldFormAccount, eulaStatus } = props;
    const isLatamSpecificData = checkIsLatamSpecificData(formAccount.tenantSpecificData);

    return (
        <>
            <table className={'table table-head-filled table-condensed'}>
                <thead>
                    <tr>
                        <th>Properties</th>
                        {oldFormAccount ? <th className={'text-color-gray'}>Account before edit</th> : ''}
                        {oldFormAccount ? <th>Account after edit</th> : <th />}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Account Id</td>
                        {oldFormAccount ? <td className={'text-color-gray'}>{oldFormAccount.company.id}</td> : ''}
                        <td>{formAccount.company.id}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.name ? 'text-bold text-color-warning' : ''}>
                        <td>Account Name</td>
                        {oldFormAccount ? <td className={'text-color-gray'}>{oldFormAccount.company.name}</td> : ''}
                        <td>{formAccount.company.name}</td>
                    </tr>
                    <tr>
                        <td>Account Tenant</td>
                        {oldFormAccount ? <td className={'text-color-gray'}>{oldFormAccount.company.tenant}</td> : ''}
                        <td>{formAccount.company.tenant}</td>
                    </tr>
                    <tr>
                        <td>Account Type</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.accountType}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.accountType}</td>
                    </tr>
                    <tr>
                        <td>Registration Channel</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.registrationChannel}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.registrationChannel}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.taxId?.value ? 'text-bold text-color-warning' : ''}>
                        <td>Tax Id</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.taxId?.value}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.taxId?.value}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.taxId?.taxType ? 'text-bold text-color-warning' : ''}>
                        <td>Type of Tax Id</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.taxId?.taxType}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.taxId?.taxType}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.dunsNumbers ? 'text-bold text-color-warning' : ''}>
                        <td>DUNS</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>
                                <ul className='icon-list margin-bottom-0'>
                                    {oldFormAccount.company.dunsNumbers?.map(it => (
                                        <li key={it} className={'margin-bottom-0'}>
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </td>
                        ) : (
                            ''
                        )}
                        <td>
                            <ul className='icon-list margin-bottom-0'>
                                {formAccount.company.dunsNumbers?.map(it => (
                                    <li key={it} className={'margin-bottom-0'}>
                                        {it}
                                    </li>
                                ))}
                            </ul>
                        </td>
                    </tr>
                    <tr className={dirtyFields?.company?.legalAddress?.line1 ? 'text-bold text-color-warning' : ''}>
                        <td>Address Line 1 (usually street & no.)</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.line1}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.line1}</td>
                    </tr>
                    <tr>
                        <td>Address Line 2 (usually unused)</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.line2}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.line2}</td>
                    </tr>
                    <tr>
                        <td>Address Line 3 (usually unused)</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.line3}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.line3}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.legalAddress?.city ? 'text-bold text-color-warning' : ''}>
                        <td>Town/City</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.city}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.city}</td>
                    </tr>
                    <tr className={dirtyFields?.company?.legalAddress?.postCode ? 'text-bold text-color-warning' : ''}>
                        <td>Post Code</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.postCode}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.postCode}</td>
                    </tr>
                    <tr
                        className={
                            dirtyFields?.company?.legalAddress?.countryCode ? 'text-bold text-color-warning' : ''
                        }
                    >
                        <td>Country Code</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.legalAddress.countryCode}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.company.legalAddress.countryCode}</td>
                    </tr>
                    <tr>
                        <td>Lifecycle State</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.company.lifeCycleState}</td>
                        ) : (
                            ''
                        )}
                        <td>
                            {dirtyFields ? (
                                formAccount.company.lifeCycleState
                            ) : (
                                <AccountStatus status={formAccount.company.lifeCycleState} />
                            )}
                        </td>
                    </tr>
                    {formAccount.company.logisticsTenant && (
                        <tr>
                            <td>LogisticsTenant</td>
                            {oldFormAccount ? (
                                <td className={'text-color-gray'}>{oldFormAccount.company.logisticsTenant}</td>
                            ) : (
                                ''
                            )}
                            <td>{formAccount.company.logisticsTenant}</td>
                        </tr>
                    )}
                    {formAccount.tenantSpecificData && isLatamSpecificData && oldFormAccount?.tenantSpecificData && (
                        <>
                            <tr className={'text-color-dark bg-lightest'} style={{ borderWidth: '10px' }}>
                                <td colSpan={oldFormAccount ? 3 : 2}>Tenant specific data</td>
                            </tr>
                            <tr>
                                <td>City Id</td>
                                {oldFormAccount ? (
                                    <td className={'text-color-gray'}>{oldFormAccount.tenantSpecificData.cityId}</td>
                                ) : (
                                    ''
                                )}
                                <td>{formAccount.tenantSpecificData.cityId}</td>
                            </tr>
                            <tr
                                className={
                                    dirtyFields?.tenantSpecificData?.neighbourhood ? 'text-bold text-color-warning' : ''
                                }
                            >
                                <td>Neighborhood</td>
                                {oldFormAccount ? (
                                    <td className={'text-color-gray'}>
                                        {oldFormAccount.tenantSpecificData.neighbourhood}
                                    </td>
                                ) : (
                                    ''
                                )}
                                <td>{formAccount.tenantSpecificData.neighbourhood}</td>
                            </tr>
                            <tr>
                                <td>State</td>
                                {oldFormAccount ? (
                                    <td className={'text-color-gray'}>{oldFormAccount.tenantSpecificData.state}</td>
                                ) : (
                                    ''
                                )}
                                <td>{formAccount.tenantSpecificData.state}</td>
                            </tr>
                            <tr
                                className={
                                    dirtyFields?.tenantSpecificData?.phoneNumber ? 'text-bold text-color-warning' : ''
                                }
                            >
                                <td>Phone number</td>
                                {oldFormAccount ? (
                                    <td className={'text-color-gray'}>
                                        {oldFormAccount.tenantSpecificData.phoneNumber}
                                    </td>
                                ) : (
                                    ''
                                )}
                                <td>{formAccount.tenantSpecificData.phoneNumber}</td>
                            </tr>
                            <tr
                                className={
                                    dirtyFields?.tenantSpecificData?.addressDetails
                                        ? 'text-bold text-color-warning'
                                        : ''
                                }
                            >
                                <td>Address details</td>
                                {oldFormAccount ? (
                                    <td className={'text-color-gray'}>
                                        {oldFormAccount.tenantSpecificData.addressDetails}
                                    </td>
                                ) : (
                                    ''
                                )}
                                <td>{formAccount.tenantSpecificData.addressDetails}</td>
                            </tr>
                        </>
                    )}
                    <tr className={'text-color-dark bg-lightest'} style={{ borderWidth: '10px' }}>
                        <td colSpan={oldFormAccount ? 3 : 2}>Contact</td>
                    </tr>
                    <tr className={dirtyFields?.contacts?.general ? 'text-bold text-color-warning' : ''}>
                        <td>General</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.contacts?.general?.email}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.contacts?.general?.email}</td>
                    </tr>
                    <tr className={dirtyFields?.contacts?.billing ? 'text-bold text-color-warning' : ''}>
                        <td>Billing</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{oldFormAccount.contacts?.billing?.email}</td>
                        ) : (
                            ''
                        )}
                        <td>{formAccount.contacts?.billing?.email}</td>
                    </tr>
                    <tr className={dirtyFields?.language ? 'text-bold text-color-warning' : ''}>
                        <td>Contact Language</td>
                        {oldFormAccount ? (
                            <td className={'text-color-gray'}>{getNativeLanguageLabel(oldFormAccount.language)}</td>
                        ) : (
                            ''
                        )}
                        <td>{getNativeLanguageLabel(formAccount.language)}</td>
                    </tr>
                </tbody>
            </table>
            {
                // hide if oldAccount is defined which means we are in the confirmation dialog
                oldFormAccount === undefined && eulaStatus && (
                    <ExpanderPanel
                        open={false}
                        title={
                            <table className='table table-condensed table-layout-fixed width-100pct'>
                                <thead>
                                    <tr className={'text-color-dark bg-lightest'}>
                                        <td colSpan={oldFormAccount ? 3 : 2} style={{ paddingLeft: '12px' }}>
                                            MAN EULA
                                        </td>
                                    </tr>
                                </thead>
                            </table>
                        }
                        headerClassName='padding-0 position-relative text-color-dark bg-lightest'
                        titleClassName='width-100pct margin-0 overflow-hidden'
                        bodyClassName='padding-0'
                        iconClassName='margin-right-5'
                        className='overflow-hidden rounded-none margin-top-20'
                    >
                        <table className='table table-condensed table-layout-fixed'>
                            <tbody>
                                {
                                    <>
                                        {eulaStatus.status && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>Status</td>
                                                <td>{eulaStatus.status}</td>
                                            </tr>
                                        )}
                                        {eulaStatus.eula_version && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>Version</td>
                                                <td>{eulaStatus.eula_version}</td>
                                            </tr>
                                        )}
                                        {eulaStatus.event_timestamp && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>Timestamp</td>
                                                <td>{eulaStatus.event_timestamp}</td>
                                            </tr>
                                        )}
                                        {eulaStatus.legal_principal && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>User</td>
                                                <td>{`${eulaStatus.legal_principal.first_name} ${eulaStatus.legal_principal.last_name}`}</td>
                                            </tr>
                                        )}
                                        {eulaStatus.legal_principal && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>Contact info</td>
                                                <td>{eulaStatus.legal_principal.contact_info}</td>
                                            </tr>
                                        )}
                                        {eulaStatus.channel && (
                                            <tr>
                                                <td style={{ paddingLeft: '12px' }}>Channel</td>
                                                <td>{eulaStatus.channel}</td>
                                            </tr>
                                        )}
                                    </>
                                }
                            </tbody>
                        </table>
                        <div className={'pull-right margin-right-20'}>
                            <RevokeEulaDialog eulaStatus={eulaStatus} />
                        </div>
                    </ExpanderPanel>
                )
            }
        </>
    );
};

// const isEulaStatusWithData = (eulaStatus: ManEulaStatus): eulaStatus is ManEulaStatusWithData =>
//     eulaStatus && eulaStatus.status !== 'UNKNOWN' && eulaStatus.status !== 'NOT_APPLICABLE';
